import { useCallback, useEffect, useState } from 'react';

/**
 * Player audio + a11y preferences. Two volume scalars in [0, 1] gating the
 * (forthcoming) audio engine's master per-class output, plus the reduced-motion
 * boolean that already gates Framer/GSAP behavior. The `musicVolume` /
 * `soundsVolume` split mirrors how players intuitively categorize game audio:
 * optional atmosphere (lobby bed, end cadences — SOUND.md's Bookend Rule
 * material) vs functional alerts (bell, page-turn, reveal trio, quickening
 * clock). 0 means muted, 1 means as-tuned-by-SOUND.md; values above 1 are not
 * supported (would risk anti-reference 1's "notification ding" loudness).
 */
export interface Preferences {
  musicVolume: number;
  soundsVolume: number;
  reducedMotion: boolean;
}

export type PreferenceKey = keyof Preferences;

export type PreferenceChange = <K extends PreferenceKey>(
  key: K,
  value: Preferences[K],
) => void;

export const DEFAULT_PREFERENCES: Preferences = {
  musicVolume: 0.5,
  soundsVolume: 1,
  reducedMotion: false,
};

const STORAGE_KEY = 'susdle:preferences';

/** Legacy v0 shape: `{ sound: boolean; reducedMotion: boolean }`. Migrated on
 *  first hydrate after this change ships; the old key is replaced in-place. */
interface PersistedShape {
  musicVolume?: unknown;
  soundsVolume?: unknown;
  reducedMotion?: unknown;
  sound?: unknown;
}

function clampUnit(n: unknown): number | null {
  if (typeof n !== 'number' || !Number.isFinite(n)) return null;
  return Math.max(0, Math.min(1, n));
}

/** Exported for main.tsx, which needs the persisted reducedMotion value
 *  before first render to stamp `<html data-reduced-motion>` pre-paint. */
export function loadStoredPreferences(): Preferences {
  if (typeof window === 'undefined') return DEFAULT_PREFERENCES;
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return DEFAULT_PREFERENCES;
  }
  if (raw === null) return DEFAULT_PREFERENCES;
  let parsed: PersistedShape;
  try {
    parsed = JSON.parse(raw) as PersistedShape;
  } catch {
    return DEFAULT_PREFERENCES;
  }
  if (typeof parsed !== 'object' || parsed === null) return DEFAULT_PREFERENCES;

  const legacyMuted = parsed.sound === false;
  const musicVolume =
    clampUnit(parsed.musicVolume) ?? (legacyMuted ? 0 : DEFAULT_PREFERENCES.musicVolume);
  const soundsVolume =
    clampUnit(parsed.soundsVolume) ?? (legacyMuted ? 0 : DEFAULT_PREFERENCES.soundsVolume);
  const reducedMotion =
    typeof parsed.reducedMotion === 'boolean'
      ? parsed.reducedMotion
      : DEFAULT_PREFERENCES.reducedMotion;

  return { musicVolume, soundsVolume, reducedMotion };
}

function persist(next: Preferences): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* localStorage blocked / quota — values still live in React state for the
       session, just don't survive a reload. Silent. */
  }
}

/**
 * Settings preferences with localStorage persistence and legacy-key migration.
 * Returns the current preferences plus a typed setter; the setter persists on
 * every change (no save button — direct manipulation per the shape brief).
 *
 * Hydration is silent: when the (forthcoming) audio engine wires onto this
 * hook, it must derive playback from state diffs *after* mount, not from the
 * initial value — same pattern as SOUND.md §5's Reconnect Guard, applied at
 * the prefs layer.
 */
export function usePreferences(): [Preferences, PreferenceChange] {
  const [preferences, setPreferences] = useState<Preferences>(loadStoredPreferences);
  /* Persistence is an effect of the state changing, never a side effect
     inside the updater — updaters must be pure (StrictMode runs them twice,
     and React is free to replay them). The mount-time persist this implies
     is deliberate: it rewrites the legacy v0 `sound: boolean` shape in place
     on first hydrate instead of waiting for the first change. */
  useEffect(() => {
    persist(preferences);
  }, [preferences]);
  const setPreference: PreferenceChange = useCallback((key, value) => {
    setPreferences((prev) => ({ ...prev, [key]: value }));
  }, []);
  return [preferences, setPreference];
}
